import React, { useState, useEffect } from 'react';

import { api } from '../../utils/requestInterceptor';
import { formatDate, formatDateForInput, getDueDateStatus } from '../../utils/dateUtils';
import { ChevronDownIcon, ChevronRightIcon, UserIcon, PlusIcon, CheckIcon, CalendarIcon, PencilIcon, ChevronUpIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import AddStepButton from '../steps/AddStepButton';
import { toast } from 'react-hot-toast';
import '../assignments/raciTooltips.css';
import useRaciAssignments from '../../hooks/useRaciAssignments';
import BulkRaciAssignment from '../assignments/BulkRaciAssignment';

// Import VideoPlayer
import VideoPlayer from '../clientPortal/VideoPlayer';

const RACI_COLORS = {
  R: 'bg-green-900/50 text-green-300 border-green-700',
  A: 'bg-blue-900/50 text-blue-300 border-blue-700',
  C: 'bg-purple-900/50 text-purple-300 border-purple-700',
  I: 'bg-yellow-900/50 text-yellow-300 border-yellow-700'
};

const RACI_TITLES = {
  R: 'These are the people that are completing the work',
  A: 'These are the people responsible for the overall project',
  C: 'These are the people that need to be consulted for the project',
  I: 'These are the people that need to be kept informed'
};

// Avatar component for user profile pictures
const UserAvatar = ({ user, roleType, onClick }) => {
  // Background colors for RACI roles
  const bgColor = 
    roleType === 'R' ? 'bg-green-100 border-green-200' : 
    roleType === 'A' ? 'bg-blue-100 border-blue-200' : 
    roleType === 'C' ? 'bg-purple-100 border-purple-200' : 
    'bg-yellow-100 border-yellow-200';
  
  // Text colors for RACI roles
  const textColor = 
    roleType === 'R' ? 'text-green-800' : 
    roleType === 'A' ? 'text-blue-800' : 
    roleType === 'C' ? 'text-purple-800' : 
    'text-yellow-800';

  // Check if user has profile image
  const hasProfileImage = user.profile_image || user.avatar;
  const profileSrc = user.profile_image || user.avatar;
  
  // Generate initials from user's name
  const getInitials = (name) => {
    if (!name) return '?';
    
    // Split the name by spaces
    const nameParts = name.split(' ').filter(part => part.length > 0);
    
    if (nameParts.length === 1) {
      // If only one name, return first two letters or just first letter if name is only one character
      return nameParts[0].substring(0, Math.min(2, nameParts[0].length)).toUpperCase();
    } else {
      // Return first letter of first name and first letter of last name
      return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
    }
  };
  
  const initials = getInitials(user.name);
  
  return (
    <div 
      className={`relative group w-7 h-7 rounded-full flex items-center justify-center border ${bgColor} ${textColor} cursor-pointer`}
      onClick={onClick}
      title={user.name}
    >
      {hasProfileImage ? (
        <img 
          src={profileSrc} 
          alt={user.name} 
          className="w-6 h-6 rounded-full object-cover"
        />
      ) : (
        <span className="text-xs font-medium">{initials}</span>
      )}
      
      {/* Tooltip that appears on hover */}
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap z-20">
        {user.name}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
      </div>
    </div>
  );
};

// Video Modal Component
const VideoModal = ({ isOpen, onClose, videoUrl, title }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>
        
        <div className="relative bg-white rounded-lg max-w-4xl w-full mx-auto shadow-xl overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-lg font-medium">{title}</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Video container with explicit height */}
          <div className="w-full" style={{ height: "480px" }}>
            <VideoPlayer url={videoUrl} />
          </div>
          
          <div className="p-4 bg-gray-50 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              This video provides instructions and information about this phase or step of the project.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProjectGridWithRaci = ({ 
  phases, 
  phaseStatusTypes = [], 
  stepStatusTypes = [], 
  handleStepClick, 
  handleStepAdded,
  handlePhaseClick,
  readOnly = false,
  projectId,
}) => {
  const [expandedPhases, setExpandedPhases] = useState({});
  const [users, setUsers] = useState([]);
  const [assignmentDropdown, setAssignmentDropdown] = useState({ open: false, entityType: null, entityId: null, roleType: null });
  const [dateEditing, setDateEditing] = useState({ open: false, entityType: null, entityId: null, currentDate: '' });
  const [statusEditing, setStatusEditing] = useState({ open: false, entityType: null, entityId: null, currentStatus: '' });
  const [updatingDate, setUpdatingDate] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  
  // Video modal state
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [videoTitle, setVideoTitle] = useState('');

  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  
  // Use our custom RACI assignments hook
  const { 
    getRaciUsers, 
    isUserAssigned, 
    updateAssignment, 
    isLoading: loadingAssignments,
    refreshAssignments: fetchAllAssignments
  } = useRaciAssignments(projectId);
  
  // Initialize all phases as expanded by default
  useEffect(() => {
    if (phases && phases.length > 0) {
      const initialExpandedState = {};
      phases.forEach(phase => {
        if (phase.id) {
          initialExpandedState[phase.id] = true;
        }
      });
      setExpandedPhases(initialExpandedState);
    }
  }, [phases]);
  
  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get('/auth/users');
        if (response.data && response.data.users) {
          setUsers(response.data.users);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    
    fetchUsers();
  }, []);
  
  const togglePhaseExpand = (phaseId, e) => {
    e.stopPropagation();
    setExpandedPhases(prev => ({
      ...prev,
      [phaseId]: !prev[phaseId]
    }));
  };
  
  const getStatusColor = (statusName, statusTypes) => {
    return statusTypes.find(s => s.name === statusName)?.color || '#6B7280';
  };
  
  // Check if loading assignments
  const isLoadingAssignments = () => loadingAssignments;
  
  // Toggle assignment dropdown
  const toggleAssignmentDropdown = (entityType, entityId, roleType, e) => {
    e.stopPropagation();
    
    if (
      assignmentDropdown.open && 
      assignmentDropdown.entityType === entityType && 
      assignmentDropdown.entityId === entityId && 
      assignmentDropdown.roleType === roleType
    ) {
      // Close dropdown if already open for this entity and role
      setAssignmentDropdown({ open: false, entityType: null, entityId: null, roleType: null });
    } else {
      // Open dropdown for this entity and role
      setAssignmentDropdown({ open: true, entityType, entityId, roleType });
    }
  };
  
  // Toggle user assignment using our new hook
  const toggleUserAssignment = async (userId, e) => {
    e.stopPropagation();
    
    const { entityType, entityId, roleType } = assignmentDropdown;
    if (!entityType || !entityId || !roleType) return;
    
    // Use our hook methods, ensuring we use them from the hook
    const currentlyAssigned = isUserAssigned(userId, entityType, entityId, roleType);
    
    // Use our hook's updateAssignment method with the correct isAdding parameter
    await updateAssignment(entityType, entityId, roleType, userId, !currentlyAssigned);
    
    // Close the dropdown after making the assignment
    setAssignmentDropdown({ open: false, entityType: null, entityId: null, roleType: null });
  };
  
  // Now, add a useEffect to handle clicks outside the dropdown
  useEffect(() => {
    // If dropdown is not open, don't do anything
    if (!assignmentDropdown.open) return;
    
    // Create a function to handle clicks outside the dropdown
    const handleClickOutside = (event) => {
      // Check if the click target is not within the dropdown
      if (event.target.closest('.assignment-dropdown') === null) {
        setAssignmentDropdown({ open: false, entityType: null, entityId: null, roleType: null });
      }
    };
    
    // Add the event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Cleanup - remove the event listener when the component unmounts or when dropdown state changes
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [assignmentDropdown.open]);
  
  // Now, let's modify the getRaciUsers function or ensure it's getting the right data
  // Let's add a wrapper function around getRaciUsers to ensure we get full user data
  const getAssignedUsers = (entityType, entityId, roleType) => {
    const assignedUsers = getRaciUsers(entityType, entityId, roleType);
    
    // Map the assigned users to the full user data from our users array
    return assignedUsers.map(assignedUser => {
      // Find the full user data from the users array
      const userData = users.find(u => u.id === assignedUser.id || u.id === assignedUser.user_id);
      
      // If we have full user data, return it, otherwise return what we have with a placeholder name
      if (userData) {
        return userData;
      } else {
        // Return the assigned user with a placeholder name if it's missing
        return {
          ...assignedUser,
          name: assignedUser.name || 'User ' + (assignedUser.id || assignedUser.user_id || 'Unknown')
        };
      }
    });
  };
  
  // Format due date for display (wrapper to handle null return for this component)
  const formatDueDate = (dateString) => {
    if (!dateString) return null;
    return formatDate(dateString);
  };
  
  // Check if due date is approaching (within 7 days) or overdue
  const getDueDateStatus = (dateString) => {
    if (!dateString) return null;
    
    const dueDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Calculate days difference
    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { status: 'overdue', className: 'bg-red-100 text-red-800' };
    } else if (diffDays <= 7) {
      return { status: 'approaching', className: 'bg-yellow-100 text-yellow-800' };
    }
    
    return { status: 'normal', className: 'bg-gray-100 text-gray-600' };
  };
  
  // Handler for opening date edit mode
  const handleDateEditClick = (entityType, entityId, currentDate, e) => {
    e.stopPropagation();
    setDateEditing({
      open: true,
      entityType,
      entityId,
      currentDate: formatDateForInput(currentDate)
    });
  };
  
  // Handler for saving the updated due date
  const handleDateSave = async (e) => {
    e.stopPropagation();
    if (!dateEditing.entityType || !dateEditing.entityId) return;
    
    try {
      setUpdatingDate(true);
      const { entityType, entityId, currentDate } = dateEditing;
      
      if (entityType === 'phase') {
        await api.put(`/phases/${entityId}`, {
          due_date: currentDate || null
        });
        toast.success('Phase due date updated');
      } else if (entityType === 'step') {
        await api.put(`/steps/${entityId}`, {
          due_date: currentDate || null
        });
        toast.success('Step due date updated');
      }
      
      // Close the date editor
      setDateEditing({ open: false, entityType: null, entityId: null, currentDate: '' });
      
      // Try to refresh the project data using the global function
      if (typeof window.fetchProjectDetails === 'function') {
        window.fetchProjectDetails();
      } else {
        // If the global function is not available, reload the page as a fallback
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error) {
      console.error('Error updating due date:', error);
      toast.error('Failed to update due date');
    } finally {
      setUpdatingDate(false);
    }
  };
  
  // Handler for closing the date editor without saving
  const handleDateCancel = (e) => {
    e.stopPropagation();
    setDateEditing({ open: false, entityType: null, entityId: null, currentDate: '' });
  };
  
  // Handle status save with cascade option for phases
  const handleStatusSave = async (newStatus, entityType, entityId, cascadeToSteps = false) => {
    if (!entityType || !entityId || !newStatus) return;
    
    try {
      // Close the status editor immediately for instant UI feedback
      setStatusEditing({ open: false, entityType: null, entityId: null, currentStatus: '' });
      
      if (entityType === 'phase') {
        // Get current phase data to ensure we're sending required fields
        const phaseResponse = await api.get(`/phases/${entityId}`);
        const currentPhase = phaseResponse.data.phase;
        
        // Show in-progress toast
        const toastId = toast.loading(`Updating phase status${cascadeToSteps ? ' and all steps' : ''}...`);
        
        // Update phase status
        await api.put(`/phases/${entityId}`, {
          name: currentPhase.name,
          description: currentPhase.description || '',
          order_index: currentPhase.order_index,
          due_date: currentPhase.due_date || null,
          status: newStatus
        });
        
        // If cascading to steps, update all step statuses
        if (cascadeToSteps && currentPhase.steps && currentPhase.steps.length > 0) {
          // Get matching status from step status types that corresponds to phase status
          const matchingStepStatus = stepStatusTypes.find(s => s.name === newStatus) ? 
            newStatus : 
            (newStatus === 'Completed' ? 'Completed' : 
             newStatus === 'Cancelled' || newStatus === 'Canceled' ? 'Cancelled' : 
             'Not Started');
          
          // Update each step
          const updatePromises = currentPhase.steps.map(async (step) => {
            try {
              // Get current step data
              const stepResponse = await api.get(`/steps/${step.id}`);
              const currentStep = stepResponse.data.step;
              
              // Update with required fields
              await api.put(`/steps/${step.id}`, {
                name: currentStep.name,
                description: currentStep.description || '',
                order_index: currentStep.order_index,
                due_date: currentStep.due_date || null,
                status: matchingStepStatus
              });
            } catch (stepError) {
              console.error(`Error updating step ${step.id}:`, stepError);
            }
          });
          
          // Wait for all step updates to complete
          await Promise.all(updatePromises);
        }
        
        // Update toast to success
        toast.success(`${entityType === 'phase' ? 'Phase' : 'Step'} status updated${cascadeToSteps ? ' with all steps' : ''}`, {
          id: toastId
        });
      } else if (entityType === 'step') {
        // Show success toast immediately for better UX
        toast.success(`${entityType === 'phase' ? 'Phase' : 'Step'} status updated`);
        
        // Get current step data to ensure we're sending required fields
        const stepResponse = await api.get(`/steps/${entityId}`);
        const currentStep = stepResponse.data.step;
        
        // Update with all the required fields explicitly mentioned in the API route
        await api.put(`/steps/${entityId}`, {
          name: currentStep.name,
          description: currentStep.description || '',
          order_index: currentStep.order_index,
          due_date: currentStep.due_date || null,
          status: newStatus
        });
      }
      
      // Use the silent option to refresh data without showing loading screen
      if (typeof window.fetchProjectDetails === 'function') {
        window.fetchProjectDetails({ silent: true });
      }
      
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
      
      // If there was an error, we may need to refresh to get back to a consistent state
      if (typeof window.fetchProjectDetails === 'function') {
        window.fetchProjectDetails();
      }
    }
  };

  // Open confirmation modal for phase status changes
  const openConfirmationModal = (newStatus, entityType, entityId) => {
    const phase = phases.find(p => p.id === entityId);
    const stepCount = phase?.steps?.length || 0;
    
    setConfirmAction(() => () => handleStatusSave(newStatus, entityType, entityId, true));
    setShowConfirmModal(true);
  };
  
  // Confirmation Modal Component
  const ConfirmationModal = ({ isOpen, onClose, onConfirm, newStatus }) => {
    if (!isOpen) return null;
    
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>
          
          <div className="relative bg-white rounded-lg max-w-md w-full mx-auto shadow-xl overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Update Status</h3>
            </div>
            
            <div className="p-6">
              <p className="text-sm text-gray-500 mb-4">
                Are you sure you want to change this phase's status? This will also update the status of all steps within this phase.
              </p>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={onClose}
                  className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancel
                </button>
                <button
                  onClick={onConfirm}
                  className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Update All
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Handler for opening status edit mode
  const handleStatusEditClick = (entityType, entityId, currentStatus, e) => {
    e.stopPropagation();
    setStatusEditing({
      open: true,
      entityType,
      entityId,
      currentStatus: currentStatus || ''
    });
  };
  
  // Handler for closing the status editor without saving
  const handleStatusCancel = (e) => {
    e.stopPropagation();
    setStatusEditing({ open: false, entityType: null, entityId: null, currentStatus: '' });
  };
  
  // Handle phase reordering
  const handlePhaseReorder = async (phaseId, direction, e) => {
    e.stopPropagation();
    try {
      setIsReordering(true);
      const response = await api.post(`/phases/${phaseId}/reorder`, { direction });
      
      if (response.data && response.data.success) {
        toast.success(response.data.message);
        // Refresh project data to reflect the new order
        if (typeof window.fetchProjectDetails === 'function') {
          window.fetchProjectDetails();
        }
      }
    } catch (error) {
      console.error('Error reordering phase:', error);
      toast.error(error.response?.data?.error || 'Failed to reorder phase');
    } finally {
      setIsReordering(false);
    }
  };
  
  // Handle step reordering
  const handleStepReorder = async (stepId, direction, e) => {
    e.stopPropagation();
    try {
      setIsReordering(true);
      const response = await api.post(`/steps/${stepId}/reorder`, { direction });
      
      if (response.data && response.data.success) {
        toast.success(response.data.message);
        // Refresh project data to reflect the new order
        if (typeof window.fetchProjectDetails === 'function') {
          window.fetchProjectDetails();
        }
      }
    } catch (error) {
      console.error('Error reordering step:', error);
      toast.error(error.response?.data?.error || 'Failed to reorder step');
    } finally {
      setIsReordering(false);
    }
  };
  
  // Check if status is active (not completed or canceled)
  const isActiveStatus = (status) => {
    if (!status) return true; // If no status, consider it active
    const lowercaseStatus = status.toLowerCase();
    return lowercaseStatus !== 'completed' && lowercaseStatus !== 'cancelled' && lowercaseStatus !== 'canceled';
  };
  
  // Filter phases and steps based on completion status
  const getFilteredPhases = () => {
    if (!phases || phases.length === 0) return [];
    
    // If showing completed items, return all phases
    if (showCompleted) return phases;
    
    // Filter phases and their steps
    return phases.map(phase => {
      // Only filter steps if phase itself is active
      if (isActiveStatus(phase.status)) {
        // Filter out completed/canceled steps
        const filteredSteps = phase.steps?.filter(step => isActiveStatus(step.status)) || [];
        
        return {
          ...phase,
          steps: filteredSteps
        };
      }
      
      // If phase is completed/canceled, return it with empty steps array
      return {
        ...phase,
        steps: []
      };
    }).filter(phase => {
      // Keep phase if it's active or has active steps
      return isActiveStatus(phase.status) || (phase.steps && phase.steps.length > 0);
    });
  };
  
  const filteredPhases = getFilteredPhases();
  
  // Open video modal
  const handleOpenVideo = (videoUrl, title, e) => {
    if (e) e.stopPropagation(); // Prevent click from propagating to parent elements
    setCurrentVideo(videoUrl);
    setVideoTitle(title);
    setShowVideoModal(true);
  };
  
  if (!filteredPhases || filteredPhases.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500">
        No phases found for this project.
      </div>
    );
  }
  
  return (
    <div className="overflow-x-auto">
      <div className="mb-4 flex justify-between items-center">
        <div>
          <BulkRaciAssignment
            phases={phases}
            projectId={projectId}
            onAssignmentsUpdated={() => {
              // Reload RACI assignments
              fetchAllAssignments();
            }}
          />
        </div>
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={showCompleted}
            onChange={() => setShowCompleted(!showCompleted)}
            className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out"
          />
          <span className="ml-2 text-sm text-gray-700">Show completed/canceled items</span>
        </label>
      </div>
      
      <table className="min-w-full divide-y divide-gray-700 border border-gray-700">
        <thead className="bg-gray-700">
          <tr>
            <th scope="col" className="sticky left-0 z-10 bg-gray-700 px-3 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider w-80 min-w-80 max-w-80">
              Phase/Step
            </th>
            <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider w-32 min-w-32">
              Status
            </th>
            <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider w-36 min-w-36">
              Due Date
            </th>
            <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider w-64 min-w-64">
              Description
            </th>
            <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-200 uppercase tracking-wider w-20 min-w-20">
              Video
            </th>
            <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-200 uppercase tracking-wider w-24 min-w-24">
              <span className="tooltip tooltip-header inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-900/50 text-green-300">
                R
                <span className="tooltiptext">{RACI_TITLES.R}</span>
              </span>
            </th>
            <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-200 uppercase tracking-wider w-24 min-w-24">
              <span className="tooltip tooltip-header inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-900/50 text-blue-300">
                A
                <span className="tooltiptext">{RACI_TITLES.A}</span>
              </span>
            </th>
            <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-200 uppercase tracking-wider w-24 min-w-24">
              <span className="tooltip tooltip-header inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-900/50 text-purple-300">
                C
                <span className="tooltiptext">{RACI_TITLES.C}</span>
              </span>
            </th>
            <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-200 uppercase tracking-wider w-24 min-w-24">
              <span className="tooltip tooltip-header inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-900/50 text-yellow-300">
                I
                <span className="tooltiptext">{RACI_TITLES.I}</span>
              </span>
            </th>
            <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-200 uppercase tracking-wider w-32 min-w-32">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {filteredPhases.map((phase, phaseIndex) => {
            const isExpanded = phase.id ? expandedPhases[phase.id] : false;
            const phaseStatusColor = getStatusColor(phase.status, phaseStatusTypes);
            const steps = Array.isArray(phase.steps) ? phase.steps : [];
            const phaseDueDate = formatDueDate(phase.due_date);
            const phaseDueDateStatus = getDueDateStatus(phase.due_date);
            
            // Determine if this is an even or odd phase for alternating colors
            const isEvenPhase = phaseIndex % 2 === 0;
            
            return (
              <React.Fragment key={phase.id || `phase-${phaseIndex}`}>
                {/* Phase Row */}
                <tr 
                  className={`phase-row ${isEvenPhase ? 'phase-row-even' : 'phase-row-odd'}`}
                >
                  <td className="sticky left-0 z-10 bg-inherit px-3 py-3 w-80 min-w-80 max-w-80">
                    <div className="flex items-center w-full">
                      {phase.id && steps.length > 0 && (
                        <button
                          onClick={(e) => togglePhaseExpand(phase.id, e)}
                          className="mr-2 text-blue-500 hover:text-blue-700 focus:outline-none flex-shrink-0"
                        >
                          {isExpanded ? (
                            <ChevronDownIcon className="h-4 w-4" />
                          ) : (
                            <ChevronRightIcon className="h-4 w-4" />
                          )}
                        </button>
                      )}
                      <div 
                        className="text-sm font-bold text-gray-900 cursor-pointer hover:text-blue-600 truncate flex-1 min-w-0"
                        onClick={() => phase.id && handlePhaseClick && handlePhaseClick(phase.id)}
                        title={phase.name}
                      >
                        {phase.name}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    {statusEditing.open && statusEditing.entityType === 'phase' && statusEditing.entityId === phase.id ? (
                      <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                        <select
                          value={statusEditing.currentStatus}
                          onChange={(e) => {
                            const newStatus = e.target.value;
                            if (newStatus) {
                              // Check if status is Completed or Cancelled/Canceled and has steps
                              if ((newStatus === 'Completed' || newStatus === 'Cancelled' || newStatus === 'Canceled') && 
                                  phase.steps && phase.steps.length > 0) {
                                openConfirmationModal(newStatus, 'phase', phase.id);
                              } else {
                                // Regular status update without cascade
                                handleStatusSave(newStatus, 'phase', phase.id);
                              }
                            }
                          }}
                          className="block w-32 text-xs border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="">Select status</option>
                          {phaseStatusTypes.map(status => (
                            <option key={status.name} value={status.name}>{status.name}</option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        {phase.status && (
                          <span
                            className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full cursor-pointer"
                            style={{
                              backgroundColor: phaseStatusColor,
                              color: 'white',
                            }}
                            onClick={(e) => handleStatusEditClick('phase', phase.id, phase.status, e)}
                          >
                            {phase.status}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    {dateEditing.open && dateEditing.entityType === 'phase' && dateEditing.entityId === phase.id ? (
                      <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="date"
                          value={dateEditing.currentDate}
                          onChange={(e) => setDateEditing({...dateEditing, currentDate: e.target.value})}
                          className="block w-32 text-xs border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex ml-2">
                          <button
                            onClick={handleDateSave}
                            disabled={updatingDate}
                            className="p-1 text-green-600 hover:text-green-800"
                          >
                            <CheckIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={handleDateCancel}
                            className="p-1 text-red-600 hover:text-red-800"
                          >
                            <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        {phaseDueDate ? (
                          <div className={`px-2 py-1 inline-flex items-center text-xs rounded ${phaseDueDateStatus?.className}`}>
                            <CalendarIcon className="h-3 w-3 mr-1" />
                            {phaseDueDate}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-500">Not set</span>
                        )}
                        {!readOnly && (
                          <button
                            className="ml-2 text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
                            onClick={(e) => handleDateEditClick('phase', phase.id, phase.due_date, e)}
                            title="Edit due date"
                          >
                            <PencilIcon className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <div className="text-xs text-gray-600 truncate font-medium" style={{ maxWidth: '300px' }}>
                      {phase.description || 'No description'}
                    </div>
                  </td>
                  
                  {/* Video column for phase */}
                  <td className="px-3 py-3 text-center">
                    {phase.video_url ? (
                      <div className="flex justify-center">
                        <button 
                          onClick={(e) => handleOpenVideo(phase.video_url, `Phase: ${phase.name}`, e)}
                          className="text-blue-600 hover:text-blue-800 focus:outline-none"
                          title="Watch video for this phase"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <div className="flex justify-center">
                        <span className="w-4 h-0.5 bg-blue-300 inline-block" title="No video available"></span>
                      </div>
                    )}
                  </td>
                  
                  {/* RACI Assignment Cells for Phase */}
                  {['R', 'A', 'C', 'I'].map(roleType => (
                    <td key={`phase-${phase.id}-${roleType}`} className="px-3 py-3 text-center relative align-top">
                      {phase.id && (
                        <div className="relative">
                          {isLoadingAssignments() ? (
                            <div className="h-6 w-6 mx-auto">
                              <svg className="animate-spin h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            </div>
                          ) : (
                            <div className="flex flex-wrap gap-1 justify-center">
                              {getAssignedUsers('phase', phase.id, roleType).map(user => (
                                <UserAvatar 
                                  key={user.id} 
                                  user={user} 
                                  roleType={roleType} 
                                  onClick={(e) => toggleAssignmentDropdown('phase', phase.id, roleType, e)}
                                />
                              ))}
                              
                              {/* Always show add button if no users or if there's space */}
                              {getAssignedUsers('phase', phase.id, roleType).length === 0 && (
                                <button
                                  className={`w-7 h-7 rounded-full border ${RACI_COLORS[roleType]} text-xs flex items-center justify-center opacity-80 hover:opacity-100`}
                                  onClick={(e) => toggleAssignmentDropdown('phase', phase.id, roleType, e)}
                                  title={`Assign ${roleType} for ${phase.name}`}
                                >
                                  <PlusIcon className="h-3 w-3" />
                                </button>
                              )}
                              
                              {/* Show add button even when users exist */}
                              {getAssignedUsers('phase', phase.id, roleType).length > 0 && (
                                <button
                                  className={`w-7 h-7 rounded-full border ${RACI_COLORS[roleType]} text-xs flex items-center justify-center bg-gray-800 opacity-80 hover:opacity-100`}
                                  onClick={(e) => toggleAssignmentDropdown('phase', phase.id, roleType, e)}
                                  title={`Manage ${roleType} assignments for ${phase.name}`}
                                >
                                  <PlusIcon className="h-3 w-3" />
                                </button>
                              )}
                              
                              {/* Assignment Dropdown */}
                              {assignmentDropdown.open && 
                                assignmentDropdown.entityType === 'phase' && 
                                assignmentDropdown.entityId === phase.id && 
                                assignmentDropdown.roleType === roleType && (
                                <div 
                                  className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-gray-700 ring-1 ring-black ring-opacity-5 z-50 max-h-60 overflow-y-auto assignment-dropdown"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="py-1" role="menu" aria-orientation="vertical">
                                    <div className="px-4 py-2 text-xs text-gray-300 border-b border-gray-600 flex justify-between items-center">
                                      <span>
                                        Role: <span className={`font-bold ${
                                          roleType === 'R' ? 'text-green-300' : 
                                          roleType === 'A' ? 'text-blue-300' : 
                                          roleType === 'C' ? 'text-purple-300' : 
                                          'text-yellow-300'
                                        }`}>{roleType}</span>
                                      </span>
                                      <button 
                                        onClick={() => setAssignmentDropdown({ open: false, entityType: null, entityId: null, roleType: null })}
                                        className="text-gray-500 hover:text-gray-700"
                                        title="Close"
                                      >
                                        <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                      </button>
                                    </div>
                                    {users.map(user => (
                                      <button
                                        key={user.id}
                                        className="w-full px-4 py-2 text-sm text-left text-gray-300 hover:bg-gray-600 flex items-center"
                                        onClick={(e) => toggleUserAssignment(user.id, e)}
                                      >
                                        <span className="mr-3">
                                          {isUserAssigned(user.id, 'phase', phase.id, roleType) ? (
                                            <CheckIcon className="h-4 w-4 text-green-400" />
                                          ) : (
                                            <UserIcon className="h-4 w-4 text-gray-400" />
                                          )}
                                        </span>
                                        {user.name}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  ))}
                  
                  <td className="px-3 py-3 whitespace-nowrap text-right text-sm font-medium">
                    {phase.id && !readOnly && (
                      <div className="flex items-center justify-end space-x-2">
                        <div className="flex flex-col">
                          <button
                            onClick={(e) => handlePhaseReorder(phase.id, 'up', e)}
                            disabled={isReordering}
                            className="p-1 text-gray-500 hover:text-blue-700 disabled:opacity-50"
                            title="Move phase up"
                          >
                            <ChevronUpIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => handlePhaseReorder(phase.id, 'down', e)}
                            disabled={isReordering}
                            className="p-1 text-gray-500 hover:text-blue-700 disabled:opacity-50"
                            title="Move phase down"
                          >
                            <ChevronDownIcon className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="flex items-center">
                          <span className="text-xs text-gray-500 mr-2" title="Phase order index">
                            #{phase.order_index}
                          </span>
                          <AddStepButton 
                            phaseId={phase.id} 
                            onStepAdded={() => handleStepAdded && handleStepAdded(phase.id)} 
                            label="Add Step"
                          />
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
                
                {/* Step Rows */}
                {phase.id && isExpanded && steps.map((step, stepIndex) => {
                  const stepStatusColor = getStatusColor(step.status, stepStatusTypes);
                  const stepDueDate = formatDueDate(step.due_date);
                  const stepDueDateStatus = getDueDateStatus(step.due_date);
                  
                  return (
                    <tr
                      key={step.id || `${phase.id}-step-${stepIndex}`}
                      className="step-row"
                    >
                      <td className="sticky left-0 z-10 bg-white px-3 py-2 w-80 min-w-80 max-w-80">
                        <div className="flex items-center w-full">
                          <div 
                            className="ml-7 text-sm text-gray-900 cursor-pointer hover:text-blue-600 truncate flex-1 min-w-0"
                            onClick={() => step.id && handleStepClick && handleStepClick(step)}
                            title={step.name}
                          >
                            {step.name}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {statusEditing.open && statusEditing.entityType === 'step' && statusEditing.entityId === step.id ? (
                          <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                            <select
                              value={statusEditing.currentStatus}
                              onChange={(e) => {
                                const newStatus = e.target.value;
                                if (newStatus) {
                                  handleStatusSave(newStatus, 'step', step.id);
                                }
                              }}
                              className="block w-32 text-xs border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <option value="">Select status</option>
                              {stepStatusTypes.map(status => (
                                <option key={status.name} value={status.name}>{status.name}</option>
                              ))}
                            </select>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            {step.status && (
                              <span
                                className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full cursor-pointer"
                                style={{
                                  backgroundColor: stepStatusColor,
                                  color: 'white',
                                }}
                                onClick={(e) => handleStatusEditClick('step', step.id, step.status, e)}
                              >
                                {step.status || 'Not Started'}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {dateEditing.open && dateEditing.entityType === 'step' && dateEditing.entityId === step.id ? (
                          <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="date"
                              value={dateEditing.currentDate}
                              onChange={(e) => setDateEditing({...dateEditing, currentDate: e.target.value})}
                              className="block w-32 text-xs border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div className="flex ml-2">
                              <button
                                onClick={handleDateSave}
                                disabled={updatingDate}
                                className="p-1 text-green-600 hover:text-green-800"
                              >
                                <CheckIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={handleDateCancel}
                                className="p-1 text-red-600 hover:text-red-800"
                              >
                                <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            {stepDueDate ? (
                              <div className={`px-2 py-1 inline-flex items-center text-xs rounded ${stepDueDateStatus?.className}`}>
                                <CalendarIcon className="h-3 w-3 mr-1" />
                                {stepDueDate}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-500">Not set</span>
                            )}
                            {!readOnly && (
                              <button
                                className="ml-2 text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
                                onClick={(e) => handleDateEditClick('step', step.id, step.due_date, e)}
                                title="Edit due date"
                              >
                                <PencilIcon className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs text-gray-500 truncate" style={{ maxWidth: '300px' }}>
                          {step.description || 'No description'}
                        </div>
                      </td>
                      
                      {/* Video column for step */}
                      <td className="px-3 py-2 text-center">
                        {step.video_url ? (
                          <div className="flex justify-center">
                            <button 
                              onClick={(e) => handleOpenVideo(step.video_url, `Step: ${step.name}`, e)}
                              className="text-blue-600 hover:text-blue-800 focus:outline-none"
                              title="Watch video for this step"
                            >
                              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </button>
                          </div>
                        ) : (
                          <div className="flex justify-center">
                            <span className="w-4 h-0.5 bg-blue-300 inline-block" title="No video available"></span>
                          </div>
                        )}
                      </td>
                      
                      {/* RACI Assignment Cells for Step */}
                      {['R', 'A', 'C', 'I'].map(roleType => (
                        <td key={`step-${step.id}-${roleType}`} className="px-3 py-2 text-center relative align-top">
                          {step.id && (
                            <div className="relative">
                              {isLoadingAssignments() ? (
                                <div className="h-6 w-6 mx-auto">
                                  <svg className="animate-spin h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                </div>
                              ) : (
                                <div className="flex flex-wrap gap-1 justify-center">
                                  {getAssignedUsers('step', step.id, roleType).map(user => (
                                    <UserAvatar 
                                      key={user.id} 
                                      user={user} 
                                      roleType={roleType} 
                                      onClick={(e) => toggleAssignmentDropdown('step', step.id, roleType, e)}
                                    />
                                  ))}
                                  
                                  {/* Always show add button if no users or if there's space */}
                                  {getAssignedUsers('step', step.id, roleType).length === 0 && (
                                    <button
                                      className={`w-7 h-7 rounded-full border ${RACI_COLORS[roleType]} text-xs flex items-center justify-center opacity-80 hover:opacity-100`}
                                      onClick={(e) => toggleAssignmentDropdown('step', step.id, roleType, e)}
                                      title={`Assign ${roleType} for ${step.name}`}
                                    >
                                      <PlusIcon className="h-3 w-3" />
                                    </button>
                                  )}
                                  
                                  {/* Show add button even when users exist */}
                                  {getAssignedUsers('step', step.id, roleType).length > 0 && (
                                    <button
                                      className={`w-7 h-7 rounded-full border ${RACI_COLORS[roleType]} text-xs flex items-center justify-center bg-gray-800 opacity-80 hover:opacity-100`}
                                      onClick={(e) => toggleAssignmentDropdown('step', step.id, roleType, e)}
                                      title={`Manage ${roleType} assignments for ${step.name}`}
                                    >
                                      <PlusIcon className="h-3 w-3" />
                                    </button>
                                  )}
                                  
                                  {/* Assignment Dropdown */}
                                  {assignmentDropdown.open && 
                                    assignmentDropdown.entityType === 'step' && 
                                    assignmentDropdown.entityId === step.id && 
                                    assignmentDropdown.roleType === roleType && (
                                    <div 
                                      className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-gray-700 ring-1 ring-black ring-opacity-5 z-50 max-h-60 overflow-y-auto assignment-dropdown"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <div className="py-1" role="menu" aria-orientation="vertical">
                                        <div className="px-4 py-2 text-xs text-gray-300 border-b border-gray-600 flex justify-between items-center">
                                          <span>
                                            Role: <span className={`font-bold ${
                                              roleType === 'R' ? 'text-green-300' : 
                                              roleType === 'A' ? 'text-blue-300' : 
                                              roleType === 'C' ? 'text-purple-300' : 
                                              'text-yellow-300'
                                            }`}>{roleType}</span>
                                          </span>
                                          <button 
                                            onClick={() => setAssignmentDropdown({ open: false, entityType: null, entityId: null, roleType: null })}
                                            className="text-gray-500 hover:text-gray-700"
                                            title="Close"
                                          >
                                            <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                          </button>
                                        </div>
                                        {users.map(user => (
                                          <button
                                            key={user.id}
                                            className="w-full px-4 py-2 text-sm text-left text-gray-300 hover:bg-gray-600 flex items-center"
                                            onClick={(e) => toggleUserAssignment(user.id, e)}
                                          >
                                            <span className="mr-3">
                                              {isUserAssigned(user.id, 'step', step.id, roleType) ? (
                                                <CheckIcon className="h-4 w-4 text-green-400" />
                                              ) : (
                                                <UserIcon className="h-4 w-4 text-gray-400" />
                                              )}
                                            </span>
                                            {user.name}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      ))}
                      
                      <td className="px-3 py-2 whitespace-nowrap text-right text-sm font-medium">
                        {step.id && !readOnly && (
                          <div className="flex items-center justify-end space-x-2">
                            <div className="flex flex-col">
                              <button
                                onClick={(e) => handleStepReorder(step.id, 'up', e)}
                                disabled={isReordering}
                                className="p-1 text-gray-500 hover:text-blue-700 disabled:opacity-50"
                                title="Move step up"
                              >
                                <ChevronUpIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={(e) => handleStepReorder(step.id, 'down', e)}
                                disabled={isReordering}
                                className="p-1 text-gray-500 hover:text-blue-700 disabled:opacity-50"
                                title="Move step down"
                              >
                                <ChevronDownIcon className="h-4 w-4" />
                              </button>
                            </div>
                            <span className="text-xs text-gray-500 mr-2" title="Step order index">
                              #{step.order_index}
                            </span>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      
      {/* Add Video Modal at the end of the component, before the closing return div */}
      {showVideoModal && currentVideo && (
        <VideoModal
          isOpen={showVideoModal}
          onClose={() => setShowVideoModal(false)}
          videoUrl={currentVideo}
          title={videoTitle}
        />
      )}
      
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={() => {
          if (confirmAction) {
            confirmAction();
            setConfirmAction(null);
            setShowConfirmModal(false);
          }
        }}
      />
    </div>
  );
};

export default ProjectGridWithRaci; 